import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyHomeTestingUsers } from './companyHomeTestingApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the Home Testing section.
 *
 * "Not Returned" is a column rather than a footnote: it is the only figure on
 * the page anyone can act on, and it belongs next to the name.
 *
 * Every user of the population is listed, including those who have never
 * ordered — they show dashes rather than zeros, so "hasn't used the benefit"
 * reads differently from "ordered a kit and never sent it back".
 */
const HomeTestingUsersTable = ({
  fetcher = fetchCompanyHomeTestingUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const ordered = row.orders > 0;
          return {
            ...row,
            ordersText: ordered ? row.orders.toLocaleString() : '-',
            awaitingText: ordered ? row.awaitingReturn.toLocaleString() : '-',
            resultsText: ordered ? row.withResults.toLocaleString() : '-',
            lastOrderDate: row.lastOrderDate || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('HomeTestingUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Kits', key: 'ordersText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Not Returned',
      key: 'awaitingText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: 'Results In', key: 'resultsText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Last Ordered',
      key: 'lastOrderDate',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

HomeTestingUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default HomeTestingUsersTable;
